import Image from 'next/image'
import React from 'react'

import { faHeadset } from '@fortawesome/pro-light-svg-icons/faHeadset'
import { Box, Container, styled, Typography } from '@mui/material'

import { Button } from '@/components/Button/Button'
import { Icon } from '@/components/Icon/Icon'
import { pxToRem } from '@/shared/styles/themes'
import { createYourOwnData } from '@/slices/HomePage/mocks/createYourOwn/data'

export const CreateYourOwn: React.FC = () => {
  const { eyebrow, headline, body, steps, imageUrl, imageAlt, ctaText, ctaLink, supportText } = createYourOwnData

  return (
    <StyledSection>
      <Container maxWidth="lg">
        <StyledContainer>
          <StyledImageContainer>
            <StyledImageWrapper>
              <Image
                src={imageUrl}
                alt={imageAlt}
                fill
                sizes="(min-width: 900px) 560px, 100vw"
                style={{ objectFit: 'contain' }}
              />
              <StyledMascot>
                <Image
                  src="/images/pixie-writing.png"
                  alt="Pixie mascot reading a glowing storybook"
                  fill
                  sizes="(min-width: 900px) 180px, 120px"
                  style={{ objectFit: 'contain' }}
                />
              </StyledMascot>
            </StyledImageWrapper>
          </StyledImageContainer>
          <StyledTextContent>
            <StyledTextContainer>
              <StyledEyebrow variant="overline">{eyebrow}</StyledEyebrow>
              <StyledHeadline variant="h2">
                {headline}
              </StyledHeadline>
              <StyledBodyText variant="body1">
                {body}
              </StyledBodyText>
              <StyledStepsList>
                {steps.map((step) => (
                  <StyledStep key={step.number}>
                    <StyledStepNumber>{step.number}</StyledStepNumber>
                    <Box>
                      <StyledStepTitle variant="h6">{step.title}</StyledStepTitle>
                      <StyledStepDescription variant="body2">
                        {step.description}
                      </StyledStepDescription>
                    </Box>
                  </StyledStep>
                ))}
              </StyledStepsList>
            </StyledTextContainer>
            <StyledCtaButton arrow primary size="large" href={ctaLink}>
              {ctaText}
            </StyledCtaButton>
            {supportText && (
              <StyledSupportText>
                <Icon icon={faHeadset} sx={{ fontSize: pxToRem(20) }} />
                <Typography variant="body2" color="text.secondary">
                  {supportText}
                </Typography>
              </StyledSupportText>
            )}
          </StyledTextContent>
        </StyledContainer>
      </Container>
    </StyledSection>
  )
}

const StyledSection = styled('section')(({ theme }) => ({
  background: 'linear-gradient(135deg, #F0E6DC 0%, #E8D5C8 100%)',
  padding: theme.spacing(5, 0),

  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(8, 0),
  },
}))

const StyledContainer = styled('div')(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(4),
  gridTemplateColumns: '1fr',
  alignItems: 'center',

  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(6),
  },
}))

const StyledImageContainer = styled('div')(({ theme }) => ({
  order: 1,
  borderRadius: pxToRem(12),

  [theme.breakpoints.up('md')]: {
    order: 1,
  },
}))

const StyledImageWrapper = styled('div')({
  position: 'relative',
  width: '100%',
  aspectRatio: '2/1',
})

const StyledMascot = styled('div')(({ theme }) => ({
  position: 'absolute',
  bottom: pxToRem(-28),
  right: pxToRem(-16),
  width: pxToRem(120),
  aspectRatio: '1/1',
  pointerEvents: 'none',
  filter: 'drop-shadow(0 8px 16px rgba(0, 0, 0, 0.12))',

  [theme.breakpoints.up('md')]: {
    bottom: pxToRem(-40),
    right: pxToRem(-32),
    width: pxToRem(180),
  },
}))

const StyledTextContent = styled('div')(({ theme }) => ({
  textAlign: 'left',
  order: 2,

  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',

  [theme.breakpoints.up('md')]: {
    order: 2,
    alignItems: 'flex-start',
  },
}))

const StyledTextContainer = styled('div')(({ theme }) => ({
  marginBottom: pxToRem(20),

  [theme.breakpoints.up('md')]: {
    marginBottom: pxToRem(25),
  },
}))

const StyledEyebrow = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 700,
  fontSize: pxToRem(16),
  letterSpacing: pxToRem(2),
  marginBottom: theme.spacing(2),
}))

const StyledHeadline = styled(Typography)(({ theme }) => ({
  textAlign: 'left',
  color: theme.palette.primary.main,
  paddingBottom: pxToRem(10),
}))

const StyledBodyText = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  lineHeight: 1.5,
  paddingBottom: pxToRem(10),
}))

const StyledStepsList = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  marginTop: theme.spacing(2),
}))

const StyledStep = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  textAlign: 'left',
}))

const StyledStepNumber = styled('div')(({ theme }) => ({
  width: pxToRem(36),
  height: pxToRem(36),
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: pxToRem(16),
  flexShrink: 0,
}))

const StyledStepTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(0.5),
}))

const StyledStepDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  lineHeight: 1.6,
}))

const StyledCtaButton = styled(Button)(({ theme }) => ({
  color: theme.palette.secondary.main,
  fontWeight: 500,
  padding: theme.spacing(2.5, 4),
}))

const StyledSupportText = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginTop: theme.spacing(2.5),
  color: theme.palette.text.secondary,
}))
